import { useEffect, useRef, useState } from 'react';
import type { GeoPoint } from '@app/shared/geo';
import type * as Leaflet from 'leaflet';
import { useGeoConfig } from '../geo/config';
import { GoogleMap, type MapRoute } from '../geo/GoogleMap';

/** Center of Poland: where an empty map starts. */
const POLAND: GeoPoint = { lat: 52.07, lng: 19.48 };
const TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

type MapProps = {
  point: GeoPoint | null;
  center?: GeoPoint | null;
  onPick?: ((p: GeoPoint) => void) | undefined;
  label: string;
  /** The mouse wheel zooms (the big map in a dialog; not a small map in a scrolled form). */
  wheelZoom?: boolean;
  /** Zoom when the map starts at `center` (no point yet). */
  centerZoom?: number;
  className?: string;
  /** "Pokaż trasę": the routes drawn (Google map only — its terms; OpenStreetMap shows none). */
  routes?: MapRoute[] | undefined;
  onRouteClick?: ((key: string) => void) | undefined;
};

/**
 * The map of the place: the Google map when the app has the Google keys (spec 2026-10-22-mapy-google §3.4), else —
 * also when Google refuses the key — the OpenStreetMap map of before. Nothing is drawn until the mode is known.
 */
export function MapPreview(props: MapProps) {
  const { config, pending } = useGeoConfig();
  const [failed, setFailed] = useState(false);
  if (pending) return <div className={props.className ?? 'map-preview'} aria-busy="true" />;
  if (config.map === 'google' && config.browserKey && !failed) {
    return <GoogleMap {...props} apiKey={config.browserKey} mapId={config.mapId ?? 'DEMO_MAP_ID'} onFail={() => setFailed(true)} />;
  }
  return <LeafletMap {...props} />;
}

/**
 * Small map of the place (owner remark 3/12): OpenStreetMap tiles with their attribution, Leaflet loaded only
 * when a map is shown. Dragging the pin or clicking the map points the place exactly (`onPick`).
 */
function LeafletMap({
  point,
  center,
  onPick,
  label,
  wheelZoom = false,
  centerZoom = 9,
  className = 'map-preview',
}: MapProps) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<Leaflet.Map | null>(null);
  const marker = useRef<Leaflet.Marker | null>(null);
  const lib = useRef<typeof Leaflet | null>(null);
  const pick = useRef(onPick);
  pick.current = onPick;
  const [failed, setFailed] = useState(false);

  // the map itself, once
  useEffect(() => {
    let cancelled = false;
    let resize: ResizeObserver | null = null;
    void Promise.all([import('leaflet'), import('leaflet/dist/leaflet.css')])
      .then(([mod]) => {
        if (cancelled || !box.current) return;
        const L = ((mod as unknown as { default?: typeof Leaflet }).default ?? mod) as typeof Leaflet;
        lib.current = L;
        const start = point ?? center ?? POLAND;
        const m = L.map(box.current, { zoomControl: true, attributionControl: true, scrollWheelZoom: wheelZoom }).setView([start.lat, start.lng], point ? 15 : center ? centerZoom : 6);
        m.attributionControl.setPrefix(false);
        L.tileLayer(TILES, { maxZoom: 19, attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' }).addTo(m);
        if (pick.current) m.on('click', (e: Leaflet.LeafletMouseEvent) => pick.current?.({ lat: round(e.latlng.lat), lng: round(e.latlng.lng) }));
        map.current = m;
        // the box may get its size later (a dialog laid out by flex, a phone turned): keep Leaflet's idea of it right,
        // or clicks land on the wrong spot
        if (typeof ResizeObserver !== 'undefined') {
          resize = new ResizeObserver(() => m.invalidateSize({ pan: false }));
          resize.observe(box.current);
        }
        place(point);
      })
      .catch(() => setFailed(true));
    return () => {
      cancelled = true;
      resize?.disconnect();
      map.current?.remove();
      map.current = null;
      marker.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function place(p: GeoPoint | null) {
    const L = lib.current;
    const m = map.current;
    if (!L || !m) return;
    if (!p) {
      marker.current?.remove();
      marker.current = null;
      return;
    }
    if (!marker.current) {
      const icon = L.divIcon({ className: 'map-pin', html: '<span></span>', iconSize: [22, 30], iconAnchor: [11, 30] });
      marker.current = L.marker([p.lat, p.lng], { icon, draggable: !!pick.current, keyboard: false, title: label }).addTo(m);
      marker.current.on('dragend', () => {
        const at = marker.current!.getLatLng();
        pick.current?.({ lat: round(at.lat), lng: round(at.lng) });
      });
    } else marker.current.setLatLng([p.lat, p.lng]);
    if (!m.getBounds().pad(-0.2).contains([p.lat, p.lng])) m.setView([p.lat, p.lng], Math.max(m.getZoom(), 14));
  }

  // a new point from outside (search result, typed) moves the pin
  useEffect(() => {
    place(point);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [point?.lat, point?.lng]);

  if (failed) return null;
  return <div ref={box} className={className} role="group" aria-label={label} data-testid="map-preview" data-map="osm" />;
}

/** 6 decimals: about 10 cm, plenty for a pin. */
const round = (n: number) => Math.round(n * 1e6) / 1e6;
